/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useRef } from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import {
  Row, Col, Button, InputGroup, Collapse,
} from 'reactstrap';

import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import {
  Field, FormikConsumer, ErrorMessage,
} from 'formik';

import * as url from '../../helpers/url_helper';
import BaseGrid from '../Common/BaseGrid';
import { get } from '../../helpers/api_helper';

function CompatibilityFieldset(props) {
  const { arrayHelpers, onLoadingChange, t } = props;
  // eslint-disable-next-line no-unused-vars

  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const [devicesBulkArea, setDevicesBulkArea] = useState(false);
  const [insertingDevices, setInsertingDevices] = useState(false);

  const devicesRef = useRef(null);

  const handleAddDevice = () => {
    arrayHelpers.push({ id: null, version: '' });
  };

  const handleInsertDevices = async (values, setFieldValue) => {
    const devices = String(values.devices_bulk || '').split('\n')
      .filter((line) => line.trim() !== '')
      .map((line) => {
        const parts = line.split(',');
        if (parts.length < 2) {
          toastr.error(`Invalid device format: ${line}`);
          return null;
        }
        return {
          brand: parts[0].trim(),
          model: parts[1].trim(),
          version: parts.slice(2).join(',').trim(),
        };
      })
      .filter((d) => d != null);

    if (devices.length === 0 || insertingDevices) return;

    setInsertingDevices(true);
    onLoadingChange(true);

    try {
      const resolvedDevices = await Promise.all(devices.map(async (device) => {
        const brand = encodeURIComponent(device.brand);
        const model = encodeURIComponent(device.model);
        const dev = await get(`/api/devices/find?brand=${brand}&model=${model}&create=1`);

        return dev == null ? null : { ...dev, version: device.version };
      }));
      const currentDevices = Array.isArray(values.devices) ? values.devices : [];

      await setFieldValue('devices', [
        ...currentDevices,
        ...resolvedDevices.filter((device) => device != null),
      ]);
      await setFieldValue('devices_bulk', '');
      setDevicesBulkArea(false);
    } catch (error) {
      toastr.error(error.message, t('Error adding devices'));
    } finally {
      setInsertingDevices(false);
      onLoadingChange(false);
    }
  };

  const clearDevices = () => {
    arrayHelpers.form.setFieldValue('devices', []);
  };

  return (
    <FormikConsumer>
      {({
        // eslint-disable-next-line no-unused-vars
        touched, errors, values, setFieldValue,
      }) => (
        <div>
          <Button type="button" color="primary" disabled={insertingDevices} onClick={() => handleAddDevice()} className="btn-sm mb-3">
            <span className="bx bx-plus" />
            {props.t('Add Device')}
          </Button>
          <Button
            type="button"
            color="primary"
            disabled={insertingDevices}
            onClick={() => {
              setDevicesBulkArea(true);
              setTimeout(() => { devicesRef.current.focus(); }, 200);
            }}
            className="btn-sm mb-3 ms-2"
          >
            <span className="bx bx-plus" />
            {props.t('Add Devices')}
          </Button>
          <Button
            type="button"
            color="secondary"
            disabled={insertingDevices}
            onClick={() => clearDevices()}
            className="btn-sm btn-soft-secondary mb-3 ms-2 text-dark"
          >
            <span className="bx bx-trash" />
            {' '}
            {props.t('Clear')}
          </Button>
          <Collapse isOpen={devicesBulkArea} className="mb-3">
            <Field name="devices_bulk" as="textarea" rows="6" className="form-control" innerRef={devicesRef} />
            <Row>
              <Col lg="9">
                <div className="help-block mt-2">
                  {props.t('Enter devices in the format:')}
                  {' '}
                  <strong>{t('Brand, Model, Version')}</strong>
                </div>
                <div className="help-block text-danger">{props.t('Devices that don\'t already exist will be created.')}</div>
              </Col>
              <Col lg="3">
                <Button
                  type="button"
                  color="info"
                  className="mt-2"
                  disabled={insertingDevices}
                  onClick={() => { handleInsertDevices(values, setFieldValue); }}
                >
                  <span className={insertingDevices ? 'mdi mdi-loading mdi-spin' : 'bx bx-check'} />
                  {insertingDevices ? props.t(' Adding models...') : props.t(' Insert')}
                </Button>
              </Col>
            </Row>
          </Collapse>
          {values && values.devices && values.devices.length > 0 && (
            <Row className="mb-3">
              <Col xs="6">
                <strong>{t('Device')}</strong>
              </Col>
              <Col xs="6">
                <strong>{t('Version(s)')}</strong>
              </Col>
            </Row>
          )}
          {values && values.devices && values.devices.map((device, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index}>
              <Row className="mb-3">
                <Col xs={device.id != null ? '6' : '12'}>
                  {device.id == null && (
                    <BaseGrid
                      url={url.GET_DEVICES}
                      hasSearch
                      config={
                      {
                        columns: {
                          id: { label: props.t('ID'), sort: true },
                          product_type: { label: props.t('Type'), sort: true },
                          brand: { label: props.t('Brand'), sort: true },
                          model: { label: props.t('Model'), sort: true },
                          actions: {
                            label: props.t('Actions'),
                            actions: [
                              {
                                key: 'edit',
                                icon: 'mdi mdi-check-bold',
                                class: 'btn btn-sm btn-soft-dark',
                                alt: props.t('Select'),
                                click: (d) => {
                                  setFieldValue(`devices[${index}]`, d);
                                },
                              },
                            ],
                          },
                        },
                        pagination: {
                          sort: 'id',
                          direction: 'desc',
                          perPage: 5,
                        },
                      }
                      }
                    />
                  )}
                  {device.id != null && (
                  <>
                    <Field name={`devices[${index}].id`} type="hidden" id={`search_${device.id}`} className="form-control" />
                    <input type="text" id={`search_${device.id}`} className="form-control" value={`${device.brand} ${device.model}`} readOnly />
                  </>
                  )}
                  <ErrorMessage
                    name={`devices[${index}].id`}
                    component="div"
                    className="text-danger pt-1"
                  />
                </Col>

                {device.id != null && (
                  <Col xs="6">
                    <InputGroup>
                      <Field name={`devices[${index}].version`} type="text" className="form-control" placeholder={t('Version info')} />
                      <Button
                        type="button"
                        onClick={() => arrayHelpers.remove(index)}
                        color="danger"
                        className="btn-soft-danger"
                      >
                        <span className="mdi mdi-close-thick" />
                      </Button>
                    </InputGroup>
                    <ErrorMessage
                      name={`devices[${index}].version`}
                      component="div"
                      className="text-danger pt-1"
                    />
                  </Col>
                )}
              </Row>
            </div>
          ))}
        </div>
      )}
    </FormikConsumer>
  );
}

CompatibilityFieldset.defaultProps = {
  onLoadingChange: () => { },
};

CompatibilityFieldset.propTypes = {
  // eslint-disable-next-line react/require-default-props
  t: PropTypes.any,
  arrayHelpers: PropTypes.object.isRequired,
  onLoadingChange: PropTypes.func,
};

export default connect(() => ({

}), {})(withTranslation()(CompatibilityFieldset));
